// Visual captcha widget (server-rendered SVG)
import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { RefreshCw, ShieldCheck } from "lucide-react";
import { SBER, API, tapAnimation } from "../utils/constants";

export const CaptchaBox = ({ onVerified, t }) => {
  const [captcha, setCaptcha] = useState(null);
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setError('');
    setAnswer('');
    try {
      const res = await axios.get(`${API}/captcha/new`);
      setCaptcha(res.data);
    } catch (e) {
      setError(t ? t('errors.connectionError') : 'Ошибка соединения');
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const verify = async () => {
    if (!captcha || answer.trim().length < 5) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/captcha/verify`, { captcha_id: captcha.captcha_id, answer });
      if (res.data.success) {
        onVerified(res.data.captcha_token);
      } else {
        setError(res.data.message || 'Неверный код');
        if (/устарела|много попыток|не найдена/i.test(res.data.message || '')) load();
      }
    } catch (e) {
      setError('Ошибка проверки, попробуйте ещё раз');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-2xl p-4" style={{ background: SBER.white }} data-testid="captcha-box">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck className="w-4 h-4" style={{ color: SBER.green }} />
        <p className="text-[13px] font-medium" style={{ color: SBER.dark }}>Подтвердите, что вы не робот</p>
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div
          className="flex-1 rounded-xl overflow-hidden flex items-center justify-center"
          style={{ background: SBER.grayPale, minHeight: 70 }}
          data-testid="captcha-image"
          dangerouslySetInnerHTML={{ __html: captcha?.svg || '' }}
        />
        <motion.button
          whileTap={tapAnimation}
          onClick={load}
          className="w-11 h-11 rounded-xl flex items-center justify-center"
          style={{ background: SBER.grayPale }}
          data-testid="captcha-refresh-btn"
          type="button"
        >
          <RefreshCw className="w-4 h-4" style={{ color: SBER.gray }} />
        </motion.button>
      </div>
      <div className="flex gap-2">
        <input
          value={answer}
          onChange={(e) => setAnswer(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && verify()}
          maxLength={5}
          placeholder="Код с картинки"
          className="flex-1 px-4 py-3 rounded-xl outline-none text-[16px] font-mono tracking-[0.3em] uppercase"
          style={{ background: SBER.grayPale, color: SBER.dark }}
          data-testid="captcha-input"
        />
        <motion.button
          whileTap={tapAnimation}
          onClick={verify}
          disabled={loading || answer.trim().length < 5}
          className="px-5 py-3 rounded-xl text-white font-semibold disabled:opacity-50"
          style={{ background: SBER.green }}
          data-testid="captcha-verify-btn"
          type="button"
        >
          {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'OK'}
        </motion.button>
      </div>
      {error && <p className="text-[12px] mt-2" style={{ color: '#EF4444' }} data-testid="captcha-error">{error}</p>}
    </div>
  );
};
